import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import AdminLayout from '@/components/admin/AdminLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ArrowLeft, Save, Eye, Loader2, BarChart3, MapPin, Building, Users } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminReportEditPage() {
  const { reportId } = useParams();
  const navigate = useNavigate();
  
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const token = localStorage.getItem('admin_token');

  useEffect(() => {
    fetchReport();
  }, [reportId]);

  const fetchReport = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/admin/reports/${reportId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReport(response.data.report);
    } catch (err) {
      setError('Failed to fetch report');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (publish = false) => {
    setSaving(true);
    setError(null);
    setSuccess(null);
    
    try {
      const updates = {
        title: report.title,
        content: report.content,
        seo: report.seo
      };
      
      if (publish) {
        updates.status = 'published';
      }
      
      await axios.put(
        `${BACKEND_URL}/api/admin/reports/${reportId}`,
        updates,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setSuccess(publish ? 'Report published!' : 'Report saved!');
      if (publish) {
        setReport({ ...report, status: 'published' });
      }
    } catch (err) {
      setError('Failed to save report');
    } finally {
      setSaving(false);
    }
  };

  const updateContent = (field, value) => {
    setReport({
      ...report,
      content: { ...report.content, [field]: value }
    });
  };

  const updateTrend = (index, field, value) => {
    const newTrends = [...report.content.keyTrends];
    newTrends[index] = { ...newTrends[index], [field]: value };
    updateContent('keyTrends', newTrends);
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex justify-center py-12">
          <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        </div>
      </AdminLayout>
    );
  }

  if (!report) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <p className="text-gray-500">Report not found</p>
          <Button onClick={() => navigate('/admin/reports')} className="mt-4">
            Back to Reports
          </Button>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" onClick={() => navigate('/admin/reports')}>
              <ArrowLeft className="w-4 h-4 mr-2" /> Back
            </Button>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Edit Report</h1>
              <div className="flex items-center gap-2 mt-1">
                <Badge variant={report.status === 'published' ? 'default' : 'secondary'}>
                  {report.status}
                </Badge>
                <span className="text-sm text-gray-500">{report.sector} · {report.period}</span>
              </div>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {report.status === 'published' && (
              <a href={`/insights/${report.slug}`} target="_blank" rel="noreferrer">
                <Button variant="outline">
                  <Eye className="w-4 h-4 mr-2" /> Preview
                </Button>
              </a>
            )}
            <Button onClick={() => handleSave(false)} disabled={saving}>
              {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
              Save Draft
            </Button>
            {report.status !== 'published' && (
              <Button onClick={() => handleSave(true)} disabled={saving} className="bg-green-600 hover:bg-green-700">
                Publish
              </Button>
            )}
          </div>
        </div>

        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg">{error}</div>}
        {success && <div className="bg-green-50 border border-green-200 text-green-700 px-4 py-3 rounded-lg">{success}</div>}

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-6">
            {/* Title & Summary */}
            <Card>
              <CardHeader>
                <CardTitle>Report Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input
                    value={report.title}
                    onChange={(e) => setReport({ ...report, title: e.target.value })}
                  />
                </div>
                <div>
                  <Label>Executive Summary</Label>
                  <Textarea
                    rows={5}
                    value={report.content?.executiveSummary || ''}
                    onChange={(e) => updateContent('executiveSummary', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>

            {/* Key Trends */}
            <Card>
              <CardHeader>
                <CardTitle>Key Trends</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {report.content?.keyTrends?.map((trend, index) => (
                  <div key={index} className="p-4 border rounded-lg space-y-3">
                    <div>
                      <Label>Trend {index + 1} Title</Label>
                      <Input
                        value={trend.title}
                        onChange={(e) => updateTrend(index, 'title', e.target.value)}
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        rows={2}
                        value={trend.description}
                        onChange={(e) => updateTrend(index, 'description', e.target.value)}
                      />
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>

            {/* Analysis Sections */}
            <Card>
              <CardHeader>
                <CardTitle>Analysis</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <Label>City/Regional Analysis</Label>
                  <Textarea
                    rows={3}
                    value={report.content?.cityAnalysis || ''}
                    onChange={(e) => updateContent('cityAnalysis', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Organiser Landscape</Label>
                  <Textarea
                    rows={3}
                    value={report.content?.organiserLandscape || ''}
                    onChange={(e) => updateContent('organiserLandscape', e.target.value)}
                  />
                </div>
                <div>
                  <Label>Forecast</Label>
                  <Textarea
                    rows={3}
                    value={report.content?.forecast || ''}
                    onChange={(e) => updateContent('forecast', e.target.value)}
                  />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar - Data Summary */}
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <BarChart3 className="w-5 h-5 mr-2" /> Data Summary
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-purple-600">
                  {report.data?.totalEvents || 0}
                </div>
                <div className="text-sm text-gray-500">Events Analyzed</div>
                
                <div className="mt-4 pt-4 border-t space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Views</span>
                    <span className="font-medium">{report.stats?.views || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Downloads</span>
                    <span className="font-medium">{report.stats?.downloads || 0}</span>
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <MapPin className="w-5 h-5 mr-2" /> Top Cities
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {report.data?.byCity?.slice(0, 5).map((city, i) => (
                    <li key={i} className="flex justify-between text-sm">
                      <span>{city._id}</span>
                      <span className="text-gray-500">{city.count} events</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Building className="w-5 h-5 mr-2" /> Top Venues
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {report.data?.byVenue?.slice(0, 5).map((venue, i) => (
                    <li key={i} className="flex justify-between text-sm">
                      <span className="truncate">{venue.venueName}</span>
                      <span className="text-gray-500 ml-2">{venue.count}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center">
                  <Users className="w-5 h-5 mr-2" /> Top Organisers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2">
                  {report.data?.byOrganiser?.slice(0, 5).map((org, i) => (
                    <li key={i} className="flex justify-between text-sm">
                      <span className="truncate">{org._id || 'Unknown'}</span>
                      <span className="text-gray-500 ml-2">{org.count}</span>
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </AdminLayout>
  );
}
